import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../services/api';
import './OptimizedImage.css';

interface OptimizedImageProps {
  src: string;
  thumbnailSrc?: string;
  alt: string;
  className?: string;
  priority?: boolean; // For above-the-fold images
  sizes?: string; // For responsive images
  /** When set, full image is only loaded when viewport width >= this (px). Below that, only thumbnail is shown. Use for mobile-friendly artwork grids. */
  loadFullAboveWidth?: number;
  onLoad?: () => void;
  onError?: () => void;
  aspectRatio?: number; // For maintaining aspect ratio during load
  draggable?: boolean;
  onContextMenu?: (event: React.MouseEvent<HTMLImageElement>) => void;
  onDragStart?: (event: React.DragEvent<HTMLImageElement>) => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  thumbnailSrc,
  alt,
  className = '',
  priority = false,
  sizes = '100vw',
  loadFullAboveWidth,
  onLoad,
  onError,
  aspectRatio,
  draggable,
  onContextMenu,
  onDragStart
}) => {
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [useThumbnail, setUseThumbnail] = useState<boolean>(true);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(priority);

  const shouldLoadFull =
    loadFullAboveWidth == null || viewportWidth >= loadFullAboveWidth;

  // Build full URLs (before effects that use them)
  const fullSrc = src.startsWith('http') ? src : `${API_BASE}${src}`;
  const fullThumbnailSrc = thumbnailSrc
    ? (thumbnailSrc.startsWith('http') ? thumbnailSrc : `${API_BASE}${thumbnailSrc}`)
    : null;

  useEffect(() => {
    if (loadFullAboveWidth == null) return;
    const onResize = (): void => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [loadFullAboveWidth]);

  // When viewport grows above threshold, upgrade from thumbnail to full image
  useEffect(() => {
    if (!shouldLoadFull || !useThumbnail || !fullThumbnailSrc) return;
    const fullImg = new Image();
    fullImg.src = fullSrc;
    fullImg.onload = () => setUseThumbnail(false);
  }, [shouldLoadFull, fullSrc, fullThumbnailSrc, useThumbnail]);

  // Intersection Observer for lazy loading (unless priority)
  useEffect(() => {
    if (priority || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading earlier for better UX
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [priority, isVisible]);

  // Preload image in browser cache
  useEffect(() => {
    if (!isVisible) return;

    const preloadImage = async (): Promise<void> => {
      try {
        const imageUrl = useThumbnail && fullThumbnailSrc ? fullThumbnailSrc : fullSrc;
        
        // Use link preload for priority images
        if (priority && 'requestIdleCallback' in window) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = imageUrl;
          document.head.appendChild(link);
        }

        // Cache in Cache API (non-blocking)
        if ('caches' in window) {
          const cacheName = priority ? 'priority-images' : 'lazy-images';
          const cache = await caches.open(cacheName);
          const cached = await cache.match(imageUrl);
          if (!cached) {
            fetch(imageUrl, { mode: 'cors' })
              .then((response) => {
                if (response.ok) {
                  cache.put(imageUrl, response.clone());
                }
              })
              .catch(() => {
                // Silently fail
              });
          }
        }
      } catch (error) {
        // Silently fail
      }
    };

    preloadImage();
  }, [isVisible, fullSrc, fullThumbnailSrc, useThumbnail, priority]);

  const handleImageLoad = (): void => {
    setImageLoaded(true);
    if (useThumbnail && fullThumbnailSrc && shouldLoadFull) {
      // After thumbnail loads, preload full image (only when viewport is large enough)
      const fullImg = new Image();
      fullImg.src = fullSrc;
      fullImg.onload = () => {
        setUseThumbnail(false);
      };
    }
    onLoad?.();
  };

  const handleError = (): void => {
    setError(true);
    if (useThumbnail && fullThumbnailSrc) {
      // Fallback to full image if thumbnail fails
      setUseThumbnail(false);
    } else {
      onError?.();
    }
  };

  const displaySrc = useThumbnail && fullThumbnailSrc ? fullThumbnailSrc : fullSrc;
  const loading = priority ? 'eager' : 'lazy';

  if (error) {
    return (
      <div className={`optimized-image-container ${className}`} ref={containerRef}>
        <div className="optimized-image-error">
          <i className="fas fa-image"></i>
          <span>Image unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`optimized-image-container ${className} ${imageLoaded ? 'loaded' : 'loading'}`}
      ref={containerRef}
      style={aspectRatio ? { aspectRatio: aspectRatio.toString() } : undefined}
    >
      {!isVisible ? (
        <div className="optimized-image-placeholder">
          <div className="placeholder-spinner"></div>
        </div>
      ) : (
        <>
          {/* Blur-up placeholder using thumbnail */}
          {useThumbnail && fullThumbnailSrc && !imageLoaded && (
            <img
              src={fullThumbnailSrc}
              alt=""
              className="optimized-image-blur"
              aria-hidden="true"
            />
          )}
          
          {/* Main image */}
          <img
            ref={imgRef}
            src={displaySrc}
            alt={alt}
            className={`optimized-image ${useThumbnail && fullThumbnailSrc ? 'optimized-image--thumbnail' : ''}`}
            loading={loading}
            onLoad={handleImageLoad}
            onError={handleError}
            decoding="async"
            fetchPriority={priority ? 'high' : 'auto'}
            draggable={draggable !== undefined ? draggable : false}
            onContextMenu={onContextMenu}
            onDragStart={onDragStart}
          />
        </>
      )}
    </div>
  );
};

export default OptimizedImage;
